import React, { useState, useEffect, useRef, PropsWithChildren } from 'react';
import {
  useAutoPosition,
  useForkRef,
  useClickOutside,
} from '@highlight-ui/utils-hooks';
import {
  parseMetadata,
  noop,
  PropsWithMetadata,
} from '@highlight-ui/utils-commons';

import { Options } from '@highlight-ui/utils-hooks/src/useAutoPosition';
import { Padding, SelectOption, SelectVariant } from '../types';

import PopoverContent from './PopoverContent';
import PopoverTrigger from './PopoverTrigger';

type PopoverProps = PropsWithChildren<
  PropsWithMetadata<{
    /**
     * Class name to apply to the element
     */
    className?: string;
    /**
     * `inline` when used outside forms and `full-width` when used inside forms.
     */
    variant?: SelectVariant;
    /**
     * Gets called whenever the visibility of the select's list has changed
     */
    onListVisibilityChange?: (visible: boolean) => void;
    /**
     * List of objects of generic type `T` extending `SelectOption`
     */
    options?: SelectOption[];
    /** Display the loading state of the list. */
    isLoading?: boolean;
    /** Property for manually toggling Popover visibility */
    isVisible?: boolean;
    /**
     * Additional padding to use when detecting collisions of the popover list of select options
     */
    popoverCollisionPadding?: Padding;
  }>
>;

const useListVisibilityChangeHandler = (
  onListVisibilityChange: NonNullable<PopoverProps['onListVisibilityChange']>,
  visible: boolean,
) => {
  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      onListVisibilityChange(visible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
};

const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      className,
      variant = 'inline',
      metadata,
      onListVisibilityChange = noop,
      isLoading = false,
      children,
      isVisible = false,
      popoverCollisionPadding,
    },
    ref,
  ) => {
    const selectTriggerRef = useRef<HTMLDivElement>(null);

    const [visible, setVisibility] = useState<boolean>(isVisible);
    const [triggerWidth, setTriggerWidth] = useState<string>();

    useEffect(() => {
      setVisibility(isVisible);
    }, [isVisible]);

    const autoPositionConfig: Partial<Options> = popoverCollisionPadding
      ? {
          flip: {
            enabled: true,
            padding: popoverCollisionPadding,
          },
        }
      : {};

    const {
      triggerRef: floatTriggerRef,
      floatRef,
      styles: floatStyles,
    } = useAutoPosition<HTMLDivElement, HTMLDivElement>(autoPositionConfig);

    const triggerRef = useForkRef<HTMLDivElement>([
      floatTriggerRef,
      (elem) => {
        if (elem) setTriggerWidth(getComputedStyle(elem).width);
      },
    ]);

    const onClickOutsideRef = useClickOutside(
      () => visible && setVisibility(false),
    );

    const finalRef = useForkRef<HTMLDivElement>([
      ref,
      selectTriggerRef,
      onClickOutsideRef,
    ]);

    useListVisibilityChangeHandler(onListVisibilityChange, visible);

    const handleTriggerClick = () => {
      setVisibility((currentVisibility) => !currentVisibility);
    };

    return (
      <div className={className} ref={finalRef} {...parseMetadata(metadata)}>
        {React.Children.map(children, (listItem) => {
          if (!React.isValidElement(listItem)) {
            return null;
          }

          switch ((listItem as JSX.Element).type.displayName) {
            case PopoverTrigger.displayName:
              return (
                <div>
                  {React.cloneElement(listItem as React.ReactElement, {
                    selectRef: selectTriggerRef,
                    triggerRef,
                    onClick: handleTriggerClick,
                    visible,
                    variant,
                  })}
                </div>
              );

            case PopoverContent.displayName:
              return (
                <>
                  {visible &&
                    React.cloneElement(listItem as React.ReactElement, {
                      popoverRef: floatRef,
                      selectTriggerRef,
                      style: {
                        ...floatStyles,
                        ...(variant === 'full-width'
                          ? { width: triggerWidth }
                          : {}),
                        ...(isLoading ? { minWidth: triggerWidth } : {}),
                      },
                    })}
                </>
              );

            default:
              return null;
          }
        })}
      </div>
    );
  },
) as React.ForwardRefExoticComponent<
  PopoverProps & React.RefAttributes<HTMLDivElement>
> & {
  Trigger: typeof PopoverTrigger;
  Content: typeof PopoverContent;
};

Popover.Trigger = PopoverTrigger;
Popover.Content = PopoverContent;
export default Popover;
